/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Grid, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { scopes } from 'src/constants/voucher_scope/voucher_scope';
import {
  GetProductsParams,
  VoucherProduct,
  VoucherScopeFromResponse
} from 'src/redux/slices/voucher/types';
import { CategoryListDropdown } from 'src/components/dropdown';
import { SelectScopeProductsModal } from './SelectScopeProductsModal';
import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch } from 'src/redux';
import { useSnackBar } from 'src/hooks';
import { ImportProductCSVModal } from './ImportProductCSVModal';
import { VoucherProductsTable } from './VoucherProductsTable';
import { EcommUserListDialog } from './EcommUserListDialog';
import { PaginationV2 } from 'src/types';
import ClearIcon from '@material-ui/icons/Clear';

interface Props {
  voucherId?: number;
  discountAmount?: number;
  discountCap?: number;
  discountType?: string;
  voucherScopes?: VoucherScopeFromResponse[];
  isCreatePage?: boolean;
  isGeneric?: boolean;
  scopeUser?: string;
  hasVoucherProducts?: boolean;
  hasVoucherStarted?: boolean;
  onScopeChange?: (
    scopes: { scope: string; scope_ids: number[] }[] | null
  ) => void;
}

const { actions: voucherActions } = slices.voucher;

export const VoucherScopeSection: React.FC<Props> = ({
  voucherId = undefined,
  voucherScopes = [],
  isCreatePage = false,
  isGeneric = false,
  hasVoucherProducts = false,
  discountAmount = 0,
  discountCap = 0,
  discountType = '',
  scopeUser = '',
  hasVoucherStarted = false,

  onScopeChange = () => {}
}) => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackBar();

  const [scopesState, setScopesState] = useState<
    { scope: string; scope_ids: number[] }[]
  >([]);

  const [selectedScope, setSelectedScope] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [selectedScopeIds, setSelectedScopeIds] = useState<number[]>([]);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [voucherProducts, setVoucherProducts] = useState<VoucherProduct[]>([]);
  const [voucherProductsMeta, setVoucherProductsMeta] = useState<PaginationV2>(
    {}
  );
  const [showImportProductCSV, setShowImportProductCSV] = useState(false);
  const [isProductsFromCSVLoading, setIsProductsFromCSVLoading] = useState(
    false
  );
  const [openScopeUserList, setOpenScopeUserList] = useState<boolean>(false);
  const [scopeUserName, setScopeUserName] = useState<string>('');
  const [showOtherScope, setShowOtherScope] = useState<boolean>(false);

  const disableScopeEdit = useMemo(() => {
    const voucherHasStarted = !isCreatePage && hasVoucherStarted;
    const voucherHasExistingScopeItems =
      scopesState[0]?.scope_ids?.filter((id) => id != null).length ?? 0;
    return voucherHasStarted || !!voucherHasExistingScopeItems;
  }, [hasVoucherStarted, isCreatePage, scopesState]);

  const dynamicOptions = useMemo(() => {
    // User scope not allowed on generic page
    if (isGeneric) {
      return scopes.filter((s) => s.value !== 'User');
    }
    return scopes;
  }, [isGeneric]);

  const userScopeID = useMemo(() => {
    if (!voucherScopes?.[0]) {
      return 0;
    }

    const { entity, entity_id } = voucherScopes?.[0];
    return entity === 'User' ? entity_id : 0;
  }, [voucherScopes]);

  const dynamicDiscountPrice = useCallback(
    (val: number | null) => {
      if (val === null) return null;

      if (discountType === 'percentage') {
        return (
          val -
          Math.min(
            val * (discountAmount / 100),
            discountCap ? discountCap : Infinity
          )
        );
      }

      return val ? val - discountAmount : null;
    },
    [discountAmount, discountCap, discountType]
  );

  const dynamicMarginPrice = (
    totalRetailPrice: number = 0,
    totalDealersPrice: number = 0
  ) => {
    if (totalDealersPrice === 0) return '0%';

    // compute discount
    let discountValue = 0;

    if (discountType === 'percentage') {
      discountValue = Math.min(
        totalRetailPrice * (discountAmount / 100),
        discountCap ? discountCap : Infinity
      );
    } else {
      discountValue = discountAmount;
    }

    const netTotalRetailPrice = totalRetailPrice - discountValue;

    let marginPercentage =
      ((netTotalRetailPrice - totalDealersPrice) / totalDealersPrice) * 100;

    // round to 3 decimals like PHP
    marginPercentage = Math.round(marginPercentage * 1000) / 1000;

    return `${marginPercentage}%`;
  };

  const onHandleSelectedChangeScope = (
    event: React.ChangeEvent<{}>,
    value: { value: string; label: string } | null,
    isAppend: boolean = false
  ) => {
    if (!value) {
      setSelectedScope(null);
      setScopesState([]);
      onScopeChange?.(null);
      return;
    }

    setSelectedScope(value); // <-- this is the missing piece

    const newScope = { scope: value.value, scope_ids: [] };
    const updatedScopes = [
      newScope,
      ...(isAppend ? scopesState.filter((s) => s.scope !== value.value) : [])
    ];

    setScopesState(updatedScopes);
    onScopeChange?.(updatedScopes);
  };

  const deleteVoucherProduct = useCallback(
    (deleteId?: number) => {
      if (!deleteId) {
        console.error('Error on deleteVoucherProduct: invalid product id');
        snackbar.show({
          severity: 'error',
          message: 'Failed to delete product'
        });
        return;
      }

      if (voucherProducts.find((p) => p?.id === deleteId)) {
        setVoucherProducts(voucherProducts.filter((p) => p?.id !== deleteId));
        setSelectedScopeIds(selectedScopeIds.filter((id) => id !== deleteId));
      }
    },
    [selectedScopeIds, snackbar, voucherProducts]
  );

  const setProducts = async (payload: GetProductsParams) => {
    const res = await fetchProductDetails(payload);

    if (res?.success) {
      setVoucherProducts(res.originalData.data || []);
      setVoucherProductsMeta(res.originalData.meta || {});
    } else {
      snackbar.show({
        severity: 'error',
        message: 'Error rendering produts.'
      });
    }
  };

  const importProductsFromCSV = async (ids: number[]) => {
    if (ids?.[0]) {
      const res = await fetchProductDetails({ ids });

      // only set ids if success
      if (res?.success) {
        const newIds = [...selectedScopeIds, ...(ids || [])];
        setSelectedScopeIds(newIds);

        // load everything
        setProducts({ ids: newIds, page: 1 });
      } else {
        console.error('Failed to fetch product details ', res?.errors); // for devs

        const errors = Object.values(res?.errors).flat()[0];

        console.error('Invalid product IDs:', errors);

        snackbar.show({
          severity: 'error',
          message: `${errors}`
        });
      }
    } else {
      console.error('No valid product id found for fetchProductDetails');
      snackbar.show({
        severity: 'error',
        message:
          'No valid product found. Please try again later or contact support.'
      });
    }
  };

  const fetchProductDetails = async (payload: GetProductsParams) => {
    setIsProductsFromCSVLoading(true);
    const res = unwrapResult(
      await dispatch(
        voucherActions.getProductsViaIdThunk({
          discount_amount: discountAmount,
          discount_cap: discountCap,
          discount_type: discountType,

          product_ids: payload.ids ?? undefined,
          keyword: payload.keyword ?? undefined,
          page: payload.page ?? undefined
        })
      )
    );

    if (res?.success) {
      setVoucherProducts(res?.originalData?.data || []);
      setVoucherProductsMeta(res?.originalData?.meta || {});
      setIsProductsFromCSVLoading(false);
      setShowImportProductCSV(false);
      return res;
    } else {
      console.error('Failed to fetch product details', res.errors); // for devs
      snackbar.show({
        severity: 'error',
        message: 'Failed to load products. Please refresh page.'
      });
    }

    setIsProductsFromCSVLoading(false);
    setShowImportProductCSV(false);
    return;
  };

  const renderAdditionalScope = () => (
    <div style={{ marginTop: '1.1em' }}>
      <Grid container style={{ rowGap: '1em' }}>
        <Grid item xs={12} md={6} style={{ paddingRight: '0.5em' }}>
          <Box display="flex" flexDirection="column">
            <Autocomplete
              disabled={!!voucherId || !!selectedScope}
              id="scope-list-dropdown"
              size="small"
              options={scopes}
              value={
                selectedScope?.value === 'User'
                  ? scopes.find((scope) => scope.value === 'Categories') || null
                  : scopes.find((scope) => scope.value === 'User') || null
              }
              fullWidth
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Choose a Scope"
                />
              )}
              onChange={(event, value) =>
                onHandleSelectedChangeScope(event, value, true)
              }
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" flexDirection="column">
            {/* Main scope field */}
            {selectedScope?.value === 'User' && (
              <CategoryListDropdown
                size="small"
                initialValue={selectedScopeIds?.[1]}
                onHandleCategories={(categoryIds: number | number[]) => {
                  const id = Array.isArray(categoryIds)
                    ? categoryIds[0]
                    : categoryIds;

                  const updatedScopes = [
                    ...scopesState.filter((s) => s.scope !== 'Categories'),
                    { scope: 'Categories', scope_ids: [id] }
                  ];

                  setScopesState(updatedScopes);
                  onScopeChange?.(updatedScopes);
                }}
                isDisabled={voucherScopes && voucherScopes.length > 0}
              />
            )}

            {selectedScope?.value === 'Categories' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  disabled={!!voucherId}
                  variant="outlined"
                  placeholder="Choose User"
                  size="small"
                  value={scopeUserName}
                  InputProps={{
                    readOnly: true,
                    endAdornment: scopeUserName && (
                      <ClearIcon
                        fontSize="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setScopeUserName('');
                          setSelectedScopeIds([]);
                          const updatedScopes = scopesState.map((s) =>
                            s.scope === 'User' ? { ...s, scope_ids: [] } : s
                          );
                          setScopesState(updatedScopes);
                          onScopeChange?.(updatedScopes);
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    )
                  }}
                  onClick={() => setOpenScopeUserList(true)}
                />
              </Grid>
            )}
          </Box>
        </Grid>
      </Grid>
    </div>
  );

  useEffect(() => {
    if (voucherScopes?.[0]) {
      const { entity } = voucherScopes[0];
      const ids = voucherScopes.map((i) => i?.entity_id);

      // check if scope is accepted
      const matchedScope = scopes.find((scope) => scope.value === entity);

      if (matchedScope) {
        setSelectedScope(matchedScope);
        setSelectedScopeIds(ids);
      }
      if (hasVoucherProducts) {
        fetchProductDetails({ ids });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherScopes]);

  useEffect(() => {
    setScopeUserName(scopeUser);
  }, [scopeUser]);

  useEffect(() => {
    if (selectedScope?.value) {
      onScopeChange?.([
        {
          scope: selectedScope?.value,
          scope_ids: selectedScopeIds
        }
      ]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScopeIds]);

  return (
    <>
      <Grid container style={{ rowGap: '1em' }}>
        <Grid item xs={12} md={6} style={{ paddingRight: '0.5em' }}>
          <Box display="flex" flexDirection="column">
            <Autocomplete
              // disabled={
              //   (!!voucherId && !!selectedScopeIds?.[0]) ||
              //   (scopesState[0]?.scope_ids?.filter((id) => id != null).length ??
              //     0) > 0
              // }
              disabled={disableScopeEdit}
              id="scope-list-dropdown"
              size="small"
              options={dynamicOptions}
              value={selectedScope}
              fullWidth
              getOptionLabel={(option) => option.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Choose a Scope"
                />
              )}
              onChange={(event, value) =>
                onHandleSelectedChangeScope(event, value, false)
              }
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box display="flex" flexDirection="column">
            {selectedScope?.value === 'Categories' && (
              <CategoryListDropdown
                size="small"
                initialValue={selectedScopeIds?.[0]} // only one category per voucher
                onHandleCategories={(categoryIds: number | number[]) => {
                  const id = Array.isArray(categoryIds)
                    ? categoryIds[0]
                    : categoryIds;

                  const updatedScopes = [
                    ...scopesState.filter((s) => s.scope !== 'Categories'),
                    { scope: 'Categories', scope_ids: [id] }
                  ];

                  setScopesState(updatedScopes);
                  onScopeChange?.(updatedScopes);
                }}
                isDisabled={voucherScopes && voucherScopes.length > 0}
              />
            )}
            {selectedScope?.value === 'Products' && (
              <Grid container style={{ height: '100%', gap: '1em' }}>
                <Grid item xs={12} md={6}>
                  <Button
                    fullWidth
                    disabled={!discountType || !discountAmount}
                    onClick={() => setShowProductsModal(!showProductsModal)}
                    variant="contained"
                    color="primary"
                    style={{ height: '100%' }}
                  >
                    Add Products
                  </Button>
                </Grid>

                <Grid item xs={12} md>
                  <Button
                    fullWidth
                    disabled={!discountType || !discountAmount}
                    onClick={() =>
                      setShowImportProductCSV(!showImportProductCSV)
                    }
                    variant="outlined"
                    color="primary"
                    style={{ height: '100%' }}
                  >
                    Select from CSV
                  </Button>
                </Grid>
              </Grid>
            )}
            {!isGeneric && selectedScope?.value === 'User' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  disabled={!!voucherId}
                  variant="outlined"
                  placeholder="Choose User"
                  size="small"
                  value={scopeUserName}
                  InputProps={{
                    readOnly: true,
                    endAdornment: scopeUserName && (
                      <ClearIcon
                        fontSize="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setScopeUserName('');
                          setSelectedScopeIds([]);
                          const updatedScopes = scopesState.map((s) =>
                            s.scope === 'User' ? { ...s, scope_ids: [] } : s
                          );
                          setScopesState(updatedScopes);
                          onScopeChange?.(updatedScopes);
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    )
                  }}
                  onClick={() => {
                    setOpenScopeUserList(true);
                  }}
                />
              </Grid>
            )}
          </Box>
        </Grid>
      </Grid>

      {(showOtherScope ||
        (voucherScopes.length > 1 &&
          (selectedScope?.value === 'Categories' ||
            selectedScope?.value === 'User'))) &&
        renderAdditionalScope()}

      {/* When the selected scope is Categories or User show the button */}
      {!isGeneric &&
      (selectedScope?.value === 'Categories' ||
        selectedScope?.value === 'User') &&
      !voucherId ? (
        <Box marginTop="1em">
          <Button
            variant="contained"
            onClick={() => {
              if (showOtherScope) {
                const scopeToRemove =
                  selectedScope?.value === 'Categories' ? 'User' : 'Categories';

                // Clone the current scopes
                const updatedScopes = scopesState.filter(
                  (scope) => scope.scope !== scopeToRemove
                );

                // If the scope to remove is "User", clear related fields
                if (scopeToRemove === 'User') {
                  setScopeUserName('');
                }

                setScopesState(updatedScopes);
                onScopeChange?.(updatedScopes);
                setShowOtherScope(false);
              } else {
                setShowOtherScope(true);
              }
            }}
          >
            {showOtherScope ? 'Remove' : 'Add'}{' '}
            {selectedScope?.value === 'Categories' ? 'User' : 'Categories'}{' '}
            Scope
          </Button>
        </Box>
      ) : null}

      {selectedScope?.value === 'Products' && selectedScopeIds?.[0] ? (
        <VoucherProductsTable
          voucherId={voucherId}
          meta={voucherProductsMeta}
          isCreatePage={isCreatePage}
          voucherProducts={voucherProducts}
          isLoading={isProductsFromCSVLoading}
          searchProduct={(payload: GetProductsParams) =>
            setProducts({ ...payload, ids: selectedScopeIds })
          }
          deleteVoucherProduct={deleteVoucherProduct}
          dynamicDiscountPrice={dynamicDiscountPrice}
          dynamicMarginPrice={dynamicMarginPrice}
          setVoucherProducts={setVoucherProducts}
          setSelectedScopeIds={setSelectedScopeIds}
        />
      ) : null}

      <SelectScopeProductsModal
        open={showProductsModal}
        discountAmount={discountAmount}
        discountCap={discountCap}
        discountType={discountType}
        selectedProductIds={voucherProducts?.map((p) => p.id || 0)}
        toggleOpen={() => setShowProductsModal(!showProductsModal)}
        saveSelectedProducts={(selectedProducts) => {
          setShowProductsModal(!showProductsModal);
          setVoucherProducts([...voucherProducts, ...selectedProducts]);
          const newIds = selectedProducts
            ?.map((p) => p.id || 0)
            .filter((id) => id !== 0);
          setSelectedScopeIds([
            ...(selectedScopeIds?.[0] ? [...selectedScopeIds] : []),
            ...newIds
          ]);
        }}
      />

      <ImportProductCSVModal
        selectedScopeIds={selectedScopeIds}
        open={showImportProductCSV}
        isLoading={isProductsFromCSVLoading}
        onClose={() => setShowImportProductCSV(false)}
        onConfirm={(ids) => importProductsFromCSV(ids)}
      />

      <EcommUserListDialog
        isVisible={openScopeUserList}
        handleClose={() => setOpenScopeUserList(false)}
        user_id={userScopeID}
        onUserSelect={(user) => {
          const newId = user?.id ?? 0;

          const updatedScopes = [
            ...scopesState.filter((s) => s.scope !== 'User'),
            { scope: 'User', scope_ids: [newId] }
          ];

          onScopeChange(updatedScopes);

          const { first_name, last_name } = user;
          setScopeUserName(`${first_name} ${last_name}`);
          setScopesState(updatedScopes);
          setOpenScopeUserList(false);
        }}
      />
    </>
  );
};

export default React.memo(VoucherScopeSection);
